import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Edit3, Plus, Save, ShieldCheck, Trash2, Truck, Users, X } from "lucide-react";
import Layout from "../../../layout/Layout";
import {
  apiHata,
  createKonteynerKural,
  createKonteynerMusteri,
  deleteKonteynerKural,
  deleteKonteynerMusteri,
  getKonteynerContext,
  getKonteynerKurallar,
  getKonteynerMusteriler,
  updateKonteynerKural,
  updateKonteynerMusteri,
} from "../../../../api/planlama/konteynerPlanApi";
import "./konteyner.css";

const emptyKural = () => ({ id: null, kod: "", ad: "", aciklama: "", tip: "zorunluDizilim", parametreler: { dizilim: "petek", yon: "" } });
const emptyMusteri = () => ({
  id: null,
  kod: "",
  ad: "",
  talimatNo: "",
  talimatKaynak: "",
  konteynerTipi: "40HC",
  maxKg: 25000,
  tabanTakoz: 0,
  araTakoz: 0,
  yanBosluk: 0,
  minTavanBosluk: 0,
  kurallar: [],
  talimatMaddeleriText: "",
  not: "",
});

/** Kural parametrelerini okunur metne çevirir. */
const parametreMetni = (tipTanim, p = {}) => {
  if (!tipTanim?.parametreler?.length) return "—";
  return tipTanim.parametreler
    .map((d) => {
      const v = p[d.key];
      if (v === undefined || v === null || v === "") return d.type === "number" ? `${d.label.replace(" (mm)", "")}: tümü` : null;
      const opt = d.options?.find((o) => o.value === v);
      return `${d.label.replace(" (mm)", "")}: ${opt ? opt.label : v}`;
    })
    .filter(Boolean)
    .join(" · ");
};

function KonteynerKuralPage() {
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState("musteriler");
  const [kurallar, setKurallar] = useState([]);
  const [musteriler, setMusteriler] = useState([]);
  const [kForm, setKForm] = useState(null);
  const [mForm, setMForm] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);

  const yetki = ctx?.yetkiler || {};
  const tipOf = useMemo(() => Object.fromEntries((ctx?.kuralTipleri || []).map((t) => [t.tip, t])), [ctx]);

  const load = useCallback(async () => {
    try {
      const [c, k, m] = await Promise.all([getKonteynerContext(), getKonteynerKurallar(), getKonteynerMusteriler()]);
      setCtx(c.data.data);
      setKurallar(k.data.data);
      setMusteriler(m.data.data);
    } catch (e) {
      setFeedback({ type: "error", message: apiHata(e) });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const ok = (message) => setFeedback({ type: "success", message });
  const fail = (e) => setFeedback({ type: "error", message: apiHata(e) });

  // ---------------- kural kaydet / sil
  const kuralKaydet = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...body } = kForm;
      const tanim = tipOf[body.tip];
      body.parametreler = Object.fromEntries((tanim?.parametreler || []).map((d) => [d.key, body.parametreler?.[d.key] ?? ""]));
      if (id) await updateKonteynerKural({ id, ...body });
      else await createKonteynerKural(body);
      setKForm(null);
      ok(id ? "Kural güncellendi." : "Kural eklendi.");
      load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };
  const kuralSil = async (k) => {
    if (!window.confirm(`“${k.ad}” kuralı pasife alınsın mı? Bu kuralı kullanan ${k.musteriSayisi} müşteri profilinde artık uygulanmaz.`)) return;
    try {
      await deleteKonteynerKural(k._id);
      ok("Kural pasife alındı.");
      load();
    } catch (err) {
      fail(err);
    }
  };

  // ---------------- müşteri kaydet / sil
  const musteriDuzenle = (m) =>
    setMForm({
      id: m._id,
      kod: m.kod,
      ad: m.ad,
      talimatNo: m.talimatNo || "",
      talimatKaynak: m.talimatKaynak || "",
      konteynerTipi: m.konteynerTipi,
      maxKg: m.maxKg,
      tabanTakoz: m.tabanTakoz,
      araTakoz: m.araTakoz,
      yanBosluk: m.yanBosluk,
      minTavanBosluk: m.minTavanBosluk || 0,
      kurallar: m.kurallar.map((r) => ({ kural: r.kural._id, varsayilanAktif: r.varsayilanAktif, madde: r.madde || "" })),
      talimatMaddeleriText: (m.talimatMaddeleri || []).join("\n"),
      not: m.not || "",
    });
  const musteriKaydet = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, talimatMaddeleriText, ...body } = mForm;
      body.talimatMaddeleri = talimatMaddeleriText.split("\n").map((x) => x.trim()).filter(Boolean);
      for (const k of ["maxKg", "tabanTakoz", "araTakoz", "yanBosluk", "minTavanBosluk"]) body[k] = Number(body[k] || 0);
      if (id) await updateKonteynerMusteri({ id, ...body });
      else await createKonteynerMusteri(body);
      setMForm(null);
      ok(id ? "Müşteri profili güncellendi." : "Müşteri profili eklendi.");
      load();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };
  const musteriSil = async (m) => {
    if (!window.confirm(`“${m.ad}” müşteri profili pasife alınsın mı?`)) return;
    try {
      await deleteKonteynerMusteri(m._id);
      ok("Müşteri profili pasife alındı.");
      load();
    } catch (err) {
      fail(err);
    }
  };
  const toggleMusteriKural = (kuralId, on) =>
    setMForm((f) => ({
      ...f,
      kurallar: on ? [...f.kurallar, { kural: kuralId, varsayilanAktif: true, madde: "" }] : f.kurallar.filter((r) => r.kural !== kuralId),
    }));
  const setMusteriKural = (kuralId, patch) =>
    setMForm((f) => ({ ...f, kurallar: f.kurallar.map((r) => (r.kural === kuralId ? { ...r, ...patch } : r)) }));

  if (!ctx) {
    return (
      <Layout>
        <div className="kyp-page">
          {feedback ? <div className={`kyp-feedback ${feedback.type}`}>{feedback.message}</div> : <div className="kyp-loading"><span className="kyp-spinner" /> Yükleniyor…</div>}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="kyp-page">
        <section className="kyp-hero">
          <div className="kyp-hero-copy">
            <span className="kyp-kicker">
              <ShieldCheck size={14} /> Planlama
            </span>
            <h1>Konteyner Kuralları ve Müşteriler</h1>
            <p>
              Kurallar ve müşteri yükleme profilleri ayrı tutulur. Bir kural bir kez tanımlanır, birçok müşteriye bağlanabilir. Müşteride kuralın
              planlama ekranında işaretli gelip gelmeyeceği ve talimattaki madde numarası belirlenir.
            </p>
          </div>
          <div className="kyp-hero-card">
            <span>Kayıtlar</span>
            <strong>
              {musteriler.length} müşteri · {kurallar.length} kural
            </strong>
            <Link to="/planning/konteyner-yukleme" className="kyp-hero-link">
              <Truck size={13} /> Yükleme planına dön
            </Link>
          </div>
        </section>

        {feedback && (
          <div className={`kyp-feedback ${feedback.type}`} role="alert">
            {feedback.message}
            <button type="button" onClick={() => setFeedback(null)} aria-label="Kapat">
              <X size={14} />
            </button>
          </div>
        )}

        <div className="kyp-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "musteriler"} onClick={() => setTab("musteriler")}>
            <Users size={15} /> Müşteri profilleri <span>{musteriler.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={tab === "kurallar"} onClick={() => setTab("kurallar")}>
            <BookOpen size={15} /> Kural kütüphanesi <span>{kurallar.length}</span>
          </button>
        </div>

        {/* ---------------- MÜŞTERİLER */}
        {tab === "musteriler" && (
          <>
            {mForm && (
              <form className="kyp-panel" onSubmit={musteriKaydet}>
                <div className="kyp-panel-header">
                  <div>
                    <h2>{mForm.id ? "Müşteri profilini düzenle" : "Yeni müşteri profili"}</h2>
                    <p>Konteyner ve takoz değerleri planlama ekranına varsayılan olarak gelir; kullanıcı orada değiştirebilir.</p>
                  </div>
                  <button type="button" className="kyp-icon-btn" onClick={() => setMForm(null)} aria-label="Kapat">
                    <X size={16} />
                  </button>
                </div>
                <div className="kyp-form-grid">
                  <Field label="Kod" id="m-kod" value={mForm.kod} onChange={(v) => setMForm({ ...mForm, kod: v })} required placeholder="RAMSTEEL" />
                  <Field label="Müşteri adı" id="m-ad" value={mForm.ad} onChange={(v) => setMForm({ ...mForm, ad: v })} required />
                  <Field label="Talimat no" id="m-tno" value={mForm.talimatNo} onChange={(v) => setMForm({ ...mForm, talimatNo: v })} placeholder="PR05-TA89" />
                  <div className="kyp-field">
                    <label htmlFor="m-tip">Konteyner tipi</label>
                    <select id="m-tip" value={mForm.konteynerTipi} onChange={(e) => setMForm({ ...mForm, konteynerTipi: e.target.value })}>
                      {Object.entries(ctx.konteynerTipleri).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="Max tonaj (kg)" id="m-kg" type="number" value={mForm.maxKg} onChange={(v) => setMForm({ ...mForm, maxKg: v })} />
                  <Field label="Taban takozu (mm)" id="m-taban" type="number" value={mForm.tabanTakoz} onChange={(v) => setMForm({ ...mForm, tabanTakoz: v })} />
                  <Field label="Ara takoz (mm)" id="m-ara" type="number" value={mForm.araTakoz} onChange={(v) => setMForm({ ...mForm, araTakoz: v })} />
                  <Field label="Toplam yan boşluk (mm)" id="m-yan" type="number" value={mForm.yanBosluk} onChange={(v) => setMForm({ ...mForm, yanBosluk: v })} />
                  <Field label="Tavana en az boşluk (mm)" id="m-tavan" type="number" value={mForm.minTavanBosluk} onChange={(v) => setMForm({ ...mForm, minTavanBosluk: v })} placeholder="örn. 1000 = 100 cm" />
                  <Field span label="Talimat kaynağı" id="m-kaynak" value={mForm.talimatKaynak} onChange={(v) => setMForm({ ...mForm, talimatKaynak: v })} placeholder="Doküman adı, revizyon, tarih" />
                </div>

                <div className="kyp-panel-body">
                  <div className="kyp-eyebrow">Uygulanan kurallar</div>
                  <div className="kyp-table-wrap">
                    <table className="kyp-table">
                      <thead>
                        <tr>
                          <th>Uygula</th>
                          <th>Kural</th>
                          <th>Tip / parametre</th>
                          <th>Ekranda varsayılan</th>
                          <th>Talimat maddesi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kurallar.map((k) => {
                          const link = mForm.kurallar.find((r) => r.kural === k._id);
                          return (
                            <tr key={k._id} className={link ? "" : "muted-row"}>
                              <td>
                                <input type="checkbox" checked={!!link} onChange={(e) => toggleMusteriKural(k._id, e.target.checked)} aria-label={`${k.ad} uygula`} />
                              </td>
                              <td>
                                <strong>{k.ad}</strong>
                                <div className="kyp-muted mono">{k.kod}</div>
                              </td>
                              <td className="kyp-muted">
                                {tipOf[k.tip]?.ad}
                                <div>{parametreMetni(tipOf[k.tip], k.parametreler)}</div>
                              </td>
                              <td>
                                <label className="kyp-check">
                                  <input type="checkbox" disabled={!link} checked={!!link?.varsayilanAktif} onChange={(e) => setMusteriKural(k._id, { varsayilanAktif: e.target.checked })} />
                                  İşaretli gelsin
                                </label>
                              </td>
                              <td>
                                <input className="kyp-inline-input" disabled={!link} value={link?.madde || ""} placeholder="Madde no" onChange={(e) => setMusteriKural(k._id, { madde: e.target.value })} aria-label="Talimat maddesi" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="kyp-form-grid one">
                  <div className="kyp-field">
                    <label htmlFor="m-madde">Talimat maddeleri (her satır bir madde; planlama ekranında gösterilir)</label>
                    <textarea id="m-madde" rows={6} value={mForm.talimatMaddeleriText} onChange={(e) => setMForm({ ...mForm, talimatMaddeleriText: e.target.value })} />
                  </div>
                  <div className="kyp-field">
                    <label htmlFor="m-not">Not</label>
                    <input id="m-not" value={mForm.not} onChange={(e) => setMForm({ ...mForm, not: e.target.value })} />
                  </div>
                </div>
                <div className="kyp-actionbar">
                  <span />
                  <div className="kyp-actions">
                    <button type="button" className="kyp-secondary-btn" onClick={() => setMForm(null)}>
                      Vazgeç
                    </button>
                    <button type="submit" className="kyp-primary-btn" disabled={saving}>
                      <Save size={15} /> Kaydet
                    </button>
                  </div>
                </div>
              </form>
            )}

            <section className="kyp-panel">
              <div className="kyp-panel-header">
                <div>
                  <h2>Müşteri yükleme profilleri</h2>
                  <p>Planlama ekranında müşteri seçildiğinde bu profil uygulanır.</p>
                </div>
                {yetki.musteriYonet && !mForm && (
                  <button type="button" className="kyp-primary-btn" onClick={() => setMForm(emptyMusteri())}>
                    <Plus size={15} /> Müşteri ekle
                  </button>
                )}
              </div>
              <div className="kyp-cards">
                {musteriler.map((m) => (
                  <article key={m._id} className="kyp-card">
                    <header>
                      <div>
                        <h3>{m.ad}</h3>
                        <span className="kyp-muted mono">
                          {m.kod}
                          {m.talimatNo ? ` · ${m.talimatNo}` : ""}
                        </span>
                      </div>
                      {yetki.musteriYonet && (
                        <div className="kyp-row-actions">
                          <button type="button" className="kyp-icon-btn" title="Düzenle" onClick={() => musteriDuzenle(m)}>
                            <Edit3 size={14} />
                          </button>
                          {m.kod !== "GENEL" && (
                            <button type="button" className="kyp-icon-btn danger" title="Pasife al" onClick={() => musteriSil(m)}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </header>
                    <dl className="kyp-kv">
                      <dt>Konteyner</dt>
                      <dd>{ctx.konteynerTipleri[m.konteynerTipi]?.name} · {m.maxKg.toLocaleString("tr-TR")} kg</dd>
                      <dt>Takoz (taban / ara)</dt>
                      <dd>{m.tabanTakoz} / {m.araTakoz} mm</dd>
                      <dt>Yan boşluk</dt>
                      <dd>{m.yanBosluk} mm</dd>
                      {!!m.minTavanBosluk && (
                        <>
                          <dt>Tavana en az boşluk</dt>
                          <dd>{m.minTavanBosluk} mm</dd>
                        </>
                      )}
                    </dl>
                    <ul className="kyp-rule-list">
                      {m.kurallar.map((r) => (
                        <li key={r.kural._id} className={r.varsayilanAktif ? "on" : "off"}>
                          <span className="dot" />
                          {r.kural.ad}
                          {r.madde && <span className="kyp-madde">{r.madde}</span>}
                          {!r.varsayilanAktif && <span className="kyp-muted"> (varsayılan kapalı)</span>}
                        </li>
                      ))}
                      {!m.kurallar.length && <li className="kyp-muted">Kural bağlı değil.</li>}
                    </ul>
                    {m.talimatMaddeleri?.length > 0 && <div className="kyp-muted">{m.talimatMaddeleri.length} talimat maddesi</div>}
                  </article>
                ))}
              </div>
            </section>
          </>
        )}

        {/* ---------------- KURALLAR */}
        {tab === "kurallar" && (
          <>
            {kForm && (
              <form className="kyp-panel" onSubmit={kuralKaydet}>
                <div className="kyp-panel-header">
                  <div>
                    <h2>{kForm.id ? "Kuralı düzenle" : "Yeni kural"}</h2>
                    <p>{tipOf[kForm.tip]?.aciklama}</p>
                  </div>
                  <button type="button" className="kyp-icon-btn" onClick={() => setKForm(null)} aria-label="Kapat">
                    <X size={16} />
                  </button>
                </div>
                <div className="kyp-form-grid">
                  <div className="kyp-field">
                    <label htmlFor="k-tip">Kural tipi</label>
                    <select id="k-tip" value={kForm.tip} onChange={(e) => setKForm({ ...kForm, tip: e.target.value, parametreler: {} })}>
                      {ctx.kuralTipleri.map((t) => (
                        <option key={t.tip} value={t.tip}>
                          {t.ad}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="Kod" id="k-kod" value={kForm.kod} onChange={(v) => setKForm({ ...kForm, kod: v })} required placeholder="Q35_2970_PETEK" />
                  <Field span label="Kural adı" id="k-ad" value={kForm.ad} onChange={(v) => setKForm({ ...kForm, ad: v })} required placeholder="Q35 × 2970 bal peteği şeklinde yüklenir" />
                  {(tipOf[kForm.tip]?.parametreler || []).map((d) =>
                    d.type === "select" ? (
                      <div className="kyp-field" key={d.key}>
                        <label htmlFor={`k-${d.key}`}>
                          {d.label}
                          {d.required ? " *" : ""}
                        </label>
                        <select
                          id={`k-${d.key}`}
                          value={kForm.parametreler?.[d.key] ?? ""}
                          required={d.required}
                          onChange={(e) => setKForm({ ...kForm, parametreler: { ...kForm.parametreler, [d.key]: e.target.value } })}
                        >
                          {!d.required && !d.options.some((o) => o.value === "") && <option value="" />}
                          {d.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <Field
                        key={d.key}
                        label={d.label + (d.required ? " *" : "")}
                        id={`k-${d.key}`}
                        type="number"
                        value={kForm.parametreler?.[d.key] ?? ""}
                        required={d.required}
                        placeholder={d.hint}
                        onChange={(v) => setKForm({ ...kForm, parametreler: { ...kForm.parametreler, [d.key]: v } })}
                      />
                    )
                  )}
                </div>
                <div className="kyp-form-grid one">
                  <div className="kyp-field">
                    <label htmlFor="k-aciklama">Açıklama</label>
                    <textarea id="k-aciklama" rows={3} value={kForm.aciklama} onChange={(e) => setKForm({ ...kForm, aciklama: e.target.value })} />
                  </div>
                </div>
                <div className="kyp-actionbar">
                  <span className="kyp-muted">Kuralı müşterilere “Müşteri profilleri” sekmesinden bağlayın.</span>
                  <div className="kyp-actions">
                    <button type="button" className="kyp-secondary-btn" onClick={() => setKForm(null)}>
                      Vazgeç
                    </button>
                    <button type="submit" className="kyp-primary-btn" disabled={saving}>
                      <Save size={15} /> Kaydet
                    </button>
                  </div>
                </div>
              </form>
            )}

            <section className="kyp-panel">
              <div className="kyp-panel-header">
                <div>
                  <h2>Kural kütüphanesi</h2>
                  <p>Motorun uyguladığı yükleme kuralları. Yeni kural tipleri yazılım tarafında eklenir; buradan mevcut tiplerle kural tanımlanır.</p>
                </div>
                {yetki.kuralYonet && !kForm && (
                  <button type="button" className="kyp-primary-btn" onClick={() => setKForm(emptyKural())}>
                    <Plus size={15} /> Kural ekle
                  </button>
                )}
              </div>
              <div className="kyp-table-wrap">
                <table className="kyp-table">
                  <thead>
                    <tr>
                      <th>Kural</th>
                      <th>Tip</th>
                      <th>Parametreler</th>
                      <th className="num">Kullanan müşteri</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {kurallar.map((k) => (
                      <tr key={k._id}>
                        <td>
                          <strong>{k.ad}</strong>
                          <div className="kyp-muted mono">{k.kod}</div>
                          {k.aciklama && <div className="kyp-muted">{k.aciklama}</div>}
                        </td>
                        <td>{tipOf[k.tip]?.ad || k.tip}</td>
                        <td className="kyp-muted">{parametreMetni(tipOf[k.tip], k.parametreler)}</td>
                        <td className="num">{k.musteriSayisi}</td>
                        <td>
                          {yetki.kuralYonet && (
                            <div className="kyp-row-actions">
                              <button
                                type="button"
                                className="kyp-icon-btn"
                                title="Düzenle"
                                onClick={() => setKForm({ id: k._id, kod: k.kod, ad: k.ad, aciklama: k.aciklama || "", tip: k.tip, parametreler: { ...(k.parametreler || {}) } })}
                              >
                                <Edit3 size={14} />
                              </button>
                              <button type="button" className="kyp-icon-btn danger" title="Pasife al" onClick={() => kuralSil(k)}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="kyp-panel-body">
                <div className="kyp-eyebrow">Tanımlı kural tipleri</div>
                <ul className="kyp-type-list">
                  {ctx.kuralTipleri.map((t) => (
                    <li key={t.tip}>
                      <strong>{t.ad}</strong> — {t.aciklama}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, id, value, onChange, type = "text", required, placeholder, span }) {
  return (
    <div className={`kyp-field${span ? " span2" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} step={type === "number" ? "any" : undefined} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default KonteynerKuralPage;
